// Project entities with relationships to users
export interface MockProject {
  id: string;
  name: string;
  description: string;
  status: 'planning' | 'in-progress' | 'on-hold' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  ownerId: string; // References MockUser
  memberIds: string[]; // References MockUser[]
  startDate: Date;
  endDate?: Date;
  dueDate: Date;
  progress: number; // 0-100
  budget?: number;
  tags: string[];
  category: string;
  createdAt: Date;
  updatedAt: Date;
}

export const mockProjects: MockProject[] = [
  {
    id: 'p1',
    name: 'Website Redesign',
    description: 'Complete redesign of company website with modern UI/UX',
    status: 'in-progress',
    priority: 'high',
    ownerId: 'u2', // Jane Smith
    memberIds: ['u1', 'u3', 'u5', 'u8'], // John, Alice, Charlie, Fiona
    startDate: new Date('2024-01-01'),
    dueDate: new Date('2024-03-31'),
    progress: 65,
    budget: 50000,
    tags: ['Web', 'Design', 'Frontend'],
    category: 'Product Development',
    createdAt: new Date('2023-12-15'),
    updatedAt: new Date('2024-01-15'),
  },
  {
    id: 'p2',
    name: 'Mobile App Development',
    description: 'Native mobile app for iOS and Android platforms',
    status: 'in-progress',
    priority: 'urgent',
    ownerId: 'u6', // Diana Prince
    memberIds: ['u1', 'u4', 'u5', 'u16'], // John, Bob, Charlie, Nina
    startDate: new Date('2023-11-01'),
    dueDate: new Date('2024-02-28'),
    progress: 80,
    budget: 120000,
    tags: ['Mobile', 'iOS', 'Android', 'React Native'],
    category: 'Product Development',
    createdAt: new Date('2023-10-20'),
    updatedAt: new Date('2024-01-14'),
  },
  {
    id: 'p3',
    name: 'CRM System Integration',
    description: 'Integrate new CRM system with existing infrastructure',
    status: 'planning',
    priority: 'medium',
    ownerId: 'u2', // Jane Smith
    memberIds: ['u4', 'u11', 'u26'], // Bob, Ian, Xavier
    startDate: new Date('2024-02-01'),
    dueDate: new Date('2024-05-31'),
    progress: 15,
    budget: 75000,
    tags: ['Backend', 'Integration', 'CRM'],
    category: 'Infrastructure',
    createdAt: new Date('2024-01-05'),
    updatedAt: new Date('2024-01-12'),
  },
  {
    id: 'p4',
    name: 'Marketing Campaign Q1',
    description: 'Launch comprehensive marketing campaign for Q1 2024',
    status: 'in-progress',
    priority: 'high',
    ownerId: 'u12', // Julia Roberts
    memberIds: ['u20'], // Rachel Green
    startDate: new Date('2024-01-01'),
    dueDate: new Date('2024-03-31'),
    progress: 50,
    budget: 30000,
    tags: ['Marketing', 'Campaign', 'Social Media'],
    category: 'Marketing',
    createdAt: new Date('2023-12-01'),
    updatedAt: new Date('2024-01-13'),
  },
  {
    id: 'p5',
    name: 'Security Audit 2024',
    description: 'Comprehensive security audit and penetration testing',
    status: 'in-progress',
    priority: 'urgent',
    ownerId: 'u21', // Sam Wilson
    memberIds: ['u7', 'u11'], // Ethan, Ian
    startDate: new Date('2024-01-10'),
    dueDate: new Date('2024-02-10'),
    progress: 40,
    budget: 25000,
    tags: ['Security', 'Compliance', 'Testing'],
    category: 'Security',
    createdAt: new Date('2023-12-20'),
    updatedAt: new Date('2024-01-15'),
  },
  {
    id: 'p6',
    name: 'Data Platform Migration',
    description: 'Migrate data platform to cloud infrastructure',
    status: 'on-hold',
    priority: 'medium',
    ownerId: 'u19', // Quincy Jones
    memberIds: ['u11', 'u24'], // Ian, Victor
    startDate: new Date('2023-12-01'),
    dueDate: new Date('2024-04-30'),
    progress: 30,
    budget: 100000,
    tags: ['Data', 'Cloud', 'Migration'],
    category: 'Infrastructure',
    createdAt: new Date('2023-11-15'),
    updatedAt: new Date('2024-01-08'),
  },
  {
    id: 'p7',
    name: 'Customer Portal Enhancement',
    description: 'Add new features to customer self-service portal',
    status: 'in-progress',
    priority: 'medium',
    ownerId: 'u27', // Yara Martinez
    memberIds: ['u1', 'u5', 'u22'], // John, Charlie, Tina
    startDate: new Date('2024-01-08'),
    dueDate: new Date('2024-03-15'),
    progress: 45,
    budget: 40000,
    tags: ['Frontend', 'Customer Experience', 'Portal'],
    category: 'Product Development',
    createdAt: new Date('2023-12-28'),
    updatedAt: new Date('2024-01-14'),
  },
  {
    id: 'p8',
    name: 'HR System Upgrade',
    description: 'Upgrade HR management system to latest version',
    status: 'planning',
    priority: 'low',
    ownerId: 'u14', // Laura Palmer
    memberIds: ['u25'], // Wendy Davis
    startDate: new Date('2024-03-01'),
    dueDate: new Date('2024-06-30'),
    progress: 10,
    budget: 35000,
    tags: ['HR', 'System Upgrade', 'Internal'],
    category: 'Internal Tools',
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-01-11'),
  },
  {
    id: 'p9',
    name: 'Sales Dashboard Redesign',
    description: 'Create new analytics dashboard for sales team',
    status: 'completed',
    priority: 'medium',
    ownerId: 'u15', // Michael Scott
    memberIds: ['u1', 'u3', 'u13', 'u30'], // John, Alice, Kevin, Dwight
    startDate: new Date('2023-10-01'),
    endDate: new Date('2023-12-31'),
    dueDate: new Date('2023-12-31'),
    progress: 100,
    budget: 20000,
    tags: ['Analytics', 'Dashboard', 'Sales'],
    category: 'Internal Tools',
    createdAt: new Date('2023-09-15'),
    updatedAt: new Date('2024-01-02'),
  },
  {
    id: 'p10',
    name: 'API v2 Development',
    description: 'Build next generation REST API with improved performance',
    status: 'in-progress',
    priority: 'high',
    ownerId: 'u26', // Xavier Woods
    memberIds: ['u4', 'u5', 'u7'], // Bob, Charlie, Ethan
    startDate: new Date('2023-11-15'),
    dueDate: new Date('2024-02-15'),
    progress: 70,
    budget: 60000,
    tags: ['API', 'Backend', 'Performance'],
    category: 'Platform',
    createdAt: new Date('2023-11-01'),
    updatedAt: new Date('2024-01-15'),
  },
  {
    id: 'p11',
    name: 'Design System Update',
    description: 'Update and expand company design system',
    status: 'in-progress',
    priority: 'medium',
    ownerId: 'u10', // Hannah Davis
    memberIds: ['u3', 'u8', 'u18'], // Alice, Fiona, Pam
    startDate: new Date('2024-01-02'),
    dueDate: new Date('2024-04-30'),
    progress: 35,
    budget: 30000,
    tags: ['Design', 'UI Components', 'Documentation'],
    category: 'Design',
    createdAt: new Date('2023-12-18'),
    updatedAt: new Date('2024-01-14'),
  },
  {
    id: 'p12',
    name: 'AI Chatbot Implementation',
    description: 'Implement AI-powered chatbot for customer support',
    status: 'planning',
    priority: 'high',
    ownerId: 'u24', // Victor Stone
    memberIds: ['u22', 'u27'], // Tina, Yara
    startDate: new Date('2024-02-15'),
    dueDate: new Date('2024-05-15'),
    progress: 20,
    budget: 80000,
    tags: ['AI', 'ML', 'Customer Support'],
    category: 'Innovation',
    createdAt: new Date('2024-01-08'),
    updatedAt: new Date('2024-01-13'),
  },
  {
    id: 'p13',
    name: 'Financial Reporting Tool',
    description: 'Build automated financial reporting and analytics tool',
    status: 'in-progress',
    priority: 'medium',
    ownerId: 'u17', // Oscar Martinez
    memberIds: ['u19', 'u29'], // Quincy, Angela
    startDate: new Date('2023-12-01'),
    dueDate: new Date('2024-03-31'),
    progress: 55,
    budget: 45000,
    tags: ['Finance', 'Analytics', 'Reporting'],
    category: 'Internal Tools',
    createdAt: new Date('2023-11-20'),
    updatedAt: new Date('2024-01-14'),
  },
  {
    id: 'p14',
    name: 'Knowledge Base Platform',
    description: 'Create internal knowledge base and documentation platform',
    status: 'planning',
    priority: 'low',
    ownerId: 'u20', // Rachel Green
    memberIds: ['u1', 'u9'], // John, George
    startDate: new Date('2024-03-01'),
    dueDate: new Date('2024-06-30'),
    progress: 5,
    budget: 25000,
    tags: ['Documentation', 'Knowledge Management', 'Internal'],
    category: 'Internal Tools',
    createdAt: new Date('2024-01-12'),
    updatedAt: new Date('2024-01-13'),
  },
  {
    id: 'p15',
    name: 'E-commerce Platform',
    description: 'Launch new e-commerce platform for online sales',
    status: 'in-progress',
    priority: 'urgent',
    ownerId: 'u6', // Diana Prince
    memberIds: ['u1', 'u4', 'u5', 'u13', 'u30'], // John, Bob, Charlie, Kevin, Dwight
    startDate: new Date('2023-10-01'),
    dueDate: new Date('2024-03-01'),
    progress: 85,
    budget: 150000,
    tags: ['E-commerce', 'Sales', 'Frontend', 'Backend'],
    category: 'Product Development',
    createdAt: new Date('2023-09-10'),
    updatedAt: new Date('2024-01-15'),
  },
  {
    id: 'p16',
    name: 'DevOps Automation',
    description: 'Implement automated CI/CD pipelines and infrastructure',
    status: 'in-progress',
    priority: 'high',
    ownerId: 'u11', // Ian Foster
    memberIds: ['u7', 'u21'], // Ethan, Sam
    startDate: new Date('2023-11-01'),
    dueDate: new Date('2024-02-28'),
    progress: 75,
    budget: 40000,
    tags: ['DevOps', 'CI/CD', 'Automation'],
    category: 'Infrastructure',
    createdAt: new Date('2023-10-25'),
    updatedAt: new Date('2024-01-14'),
  },
  {
    id: 'p17',
    name: 'Legal Compliance Review',
    description: 'Comprehensive legal and compliance review for GDPR',
    status: 'completed',
    priority: 'urgent',
    ownerId: 'u23', // Uma Thurman
    memberIds: ['u14', 'u21'], // Laura, Sam
    startDate: new Date('2023-09-01'),
    endDate: new Date('2023-12-15'),
    dueDate: new Date('2023-12-15'),
    progress: 100,
    budget: 35000,
    tags: ['Legal', 'Compliance', 'GDPR'],
    category: 'Compliance',
    createdAt: new Date('2023-08-20'),
    updatedAt: new Date('2023-12-20'),
  },
  {
    id: 'p18',
    name: 'Employee Onboarding System',
    description: 'Digital onboarding system for new employees',
    status: 'in-progress',
    priority: 'medium',
    ownerId: 'u14', // Laura Palmer
    memberIds: ['u1', 'u9'], // John, George
    startDate: new Date('2024-01-05'),
    dueDate: new Date('2024-04-15'),
    progress: 40,
    budget: 30000,
    tags: ['HR', 'Onboarding', 'Internal'],
    category: 'HR',
    createdAt: new Date('2023-12-22'),
    updatedAt: new Date('2024-01-13'),
  },
  {
    id: 'p19',
    name: 'Performance Monitoring',
    description: 'Implement application performance monitoring and alerting',
    status: 'planning',
    priority: 'high',
    ownerId: 'u11', // Ian Foster
    memberIds: ['u4', 'u7', 'u26'], // Bob, Ethan, Xavier
    startDate: new Date('2024-02-01'),
    dueDate: new Date('2024-04-30'),
    progress: 15,
    budget: 35000,
    tags: ['Monitoring', 'Performance', 'DevOps'],
    category: 'Infrastructure',
    createdAt: new Date('2024-01-09'),
    updatedAt: new Date('2024-01-12'),
  },
  {
    id: 'p20',
    name: 'Intern Training Program',
    description: 'Develop comprehensive training program for engineering interns',
    status: 'planning',
    priority: 'low',
    ownerId: 'u2', // Jane Smith
    memberIds: ['u1', 'u28'], // John, Zack
    startDate: new Date('2024-05-01'),
    dueDate: new Date('2024-08-31'),
    progress: 10,
    budget: 15000,
    tags: ['Training', 'Education', 'Interns'],
    category: 'HR',
    createdAt: new Date('2024-01-11'),
    updatedAt: new Date('2024-01-14'),
  },
];
